<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { listAdminCalendars, ApiError, type AdminCalendar } from '@/api'
import { formatDate, ownerName } from '@/admin/format'
import DrillList from '@/components/DrillList.vue'

const { t } = useI18n()
const calendars = ref<AdminCalendar[]>([])
const total = ref(0)
const page = ref(1)
const pageSize = 25
const q = ref('')
const subscribedFilter = ref<'all' | 'true' | 'false'>('all')
const loading = ref(true)
const loadError = ref('')

// Owner > account > calendar; a level with one value is skipped.
const calendarLevels = [
  { label: (cal: AdminCalendar) => ownerName(cal.owner) },
  { label: (cal: AdminCalendar) => cal.account.name },
]
const calendarTexts = (cal: AdminCalendar) => [cal.name, cal.account.name, ownerName(cal.owner)]

async function load() {
  loading.value = true
  loadError.value = ''
  try {
    const result = await listAdminCalendars({
      page: page.value,
      pageSize,
      q: q.value || undefined,
      subscribed: subscribedFilter.value === 'all' ? undefined : subscribedFilter.value === 'true',
    })
    calendars.value = result.items
    total.value = result.total
  } catch (e) {
    loadError.value = e instanceof ApiError ? e.message : t('admin.loadCalendarsFailed')
  } finally {
    loading.value = false
  }
}

// (the drill list rests the search before it reports it)
function onSearch(value: string) {
  q.value = value
  page.value = 1
  load()
}

watch(subscribedFilter, () => {
  page.value = 1
  load()
})

function prevPage() {
  if (page.value > 1) {
    page.value -= 1
    load()
  }
}

function nextPage() {
  if (page.value * pageSize < total.value) {
    page.value += 1
    load()
  }
}

onMounted(load)
</script>

<template>
  <div>
    <p class="hint">{{ t('admin.calendarsHint') }}</p>
    <div class="filters">
      <select v-model="subscribedFilter">
        <option value="all">{{ t('admin.filterAll') }}</option>
        <option value="true">{{ t('admin.filterSubscribed') }}</option>
        <option value="false">{{ t('admin.filterUnsubscribed') }}</option>
      </select>
    </div>

    <p v-if="loadError" class="error">{{ loadError }}</p>
    <p v-if="loading">{{ t('common.loading') }}</p>
    <DrillList
      v-if="!loadError"
      :items="calendars"
      :levels="calendarLevels"
      :texts="calendarTexts"
      :item-key="(cal: AdminCalendar) => cal.id"
      :local="false"
      query-key="calendars"
      :placeholder="t('admin.searchPlaceholder')"
      @search="onSearch"
    >
      <template #item="{ item: cal }">
        <dl>
          <div class="field">
            <dt>{{ t('admin.col.calendar') }}</dt>
            <dd>{{ cal.name }}</dd>
          </div>
          <div class="field">
            <dt>{{ t('admin.col.account') }}</dt>
            <dd>{{ cal.account.name }}</dd>
          </div>
          <div class="field">
            <dt>{{ t('admin.col.owner') }}</dt>
            <dd>{{ ownerName(cal.owner) }}</dd>
          </div>
          <div class="field">
            <dt>{{ t('admin.col.subscribed') }}</dt>
            <dd>
              <span class="badge" :class="{ yes: cal.subscribed }">{{ cal.subscribed ? t('admin.yes') : t('admin.no') }}</span>
            </dd>
          </div>
          <div class="field">
            <dt>{{ t('admin.col.lastSeen') }}</dt>
            <dd>{{ formatDate(cal.last_seen_at) }}</dd>
          </div>
        </dl>
      </template>
    </DrillList>

    <div v-if="!loading && total > pageSize" class="pagination">
      <button :disabled="page <= 1" @click="prevPage">{{ t('admin.prev') }}</button>
      <span class="hint"
        >{{
          t('admin.pageOf', {
            page,
            pages: Math.max(1, Math.ceil(total / pageSize)),
            total,
          })
        }}</span
      >
      <button :disabled="page * pageSize >= total" @click="nextPage">{{ t('admin.next') }}</button>
    </div>
  </div>
</template>

<style scoped>
.filters {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.badge {
  font-size: 0.75rem;
  padding: 0.1rem 0.4rem;
  border-radius: 0.5rem;
  background: var(--tg-secondary-bg-color);
  color: var(--tg-hint-color);
}

.badge.yes {
  color: #34c759;
}

.pagination {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
}

.hint {
  color: var(--tg-hint-color);
  font-size: 0.85rem;
}

.error {
  color: var(--tg-destructive-text-color);
}
</style>
